import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneMockup } from '../interactive/PhoneMockup';
import { appScreenshots, mascot } from '../../data/mock';
import { Download } from 'lucide-react';
import { ParticleBackground } from '../ui/ParticleBackground';
import { useTranslation } from 'react-i18next';

export const HeroPainHook = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentPainIndex, setCurrentPainIndex] = useState(0);
  const painPoints = t('heroPainHook.painPoints', { returnObjects: true }) || [];

  useEffect(() => {
    if (painPoints.length === 0) return;
    const interval = setInterval(() => {
      setCurrentPainIndex((prev) => (prev + 1) % painPoints.length);
    }, 4000); // Rotate every 4 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-24 overflow-hidden bg-slate-950 min-h-screen flex items-center">
      {/* Particle & Spotlight Background */}
      <ParticleBackground />
      
      <div className="w-full max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {/* Left: Text Content */}
          <div className="text-center flex flex-col justify-center items-center">
            
            {/* Typewriter / Crossfade Pain Points */}
            <div className="w-full h-28 sm:h-24 lg:h-32 mb-6 relative flex justify-center">
              <AnimatePresence mode="wait">
                <motion.h2
                  key={currentPainIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white/90 tracking-tight absolute inset-0 flex items-center justify-center text-center"
                >
                  {painPoints[currentPainIndex]}
                </motion.h2>
              </AnimatePresence>
            </div>
            {/* The Solution */}

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tighter mb-8 drop-shadow-sm text-center"
            >
              {t('heroPainHook.solution1')} <br />
              {t('heroPainHook.solution2')}
            </motion.h1>

            {/* Monkeys Centered */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="flex justify-center items-end gap-4 sm:gap-6 mt-10 w-full"
            >
              <motion.img 
                animate={{ y: [0, -7, 0] }}
                transition={{ repeat: Infinity, duration: 3.8, ease: "easeInOut", delay: 0.2 }}
                src={mascot.basic} 
                alt="Monito Free" 
                className="w-32 lg:w-40 drop-shadow-2xl mb-1 z-0" 
              />
              <motion.img 
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                src={mascot.main} 
                alt="Monito Mochilero" 
                className="w-36 lg:w-48 drop-shadow-2xl z-10"
              />
              <motion.img 
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut", delay: 0.5 }}
                src={mascot.pro} 
                alt="Monito Business" 
                className="w-36 lg:w-48 drop-shadow-2xl z-0"
              />
            </motion.div>

          </div>

          {/* Right: Visual (Phone only now) */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="relative flex items-center justify-center lg:justify-end mt-12 lg:mt-0"
          >
            {/* Phone Mockup running the app perfectly */}
            <div className="relative z-10 drop-shadow-2xl">
              <PhoneMockup currentScreen={appScreenshots.map} />
            </div>
            
            {/* Decorative elements around the phone */}
            <div className="absolute -right-6 top-10 w-48 h-48 bg-[#0DA2E7]/30 rounded-full blur-3xl -z-10" />
            <div className="absolute -left-10 bottom-10 w-56 h-56 bg-[#F97316]/30 rounded-full blur-3xl -z-10" />
          </motion.div>

        </div>
      </div>
    </section>
  );
};
